import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Search, CheckCircle2, Circle } from "lucide-react";
import { useTheme } from "@context/ThemeContext";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";

/**
 * 侧边抽屉式资产选择器
 *
 * Props:
 * - isOpen: boolean - 是否打开
 * - onClose: () => void - 关闭回调
 * - label: string - 标签（如 "角色"）
 * - icon: Component - 图标组件
 * - options: Array - 资产列表
 * - selectedIds: Array - 已选中的资产ID
 * - onChange: (ids) => void - 选中变化回调
 * - variantIndexes: Array - 变体索引列表
 * - onVariantChange: (indexes) => void - 变体变化回调
 * - maxSelection: number - 可选上限（可选）
 * - onSelectionLimit: (limit) => void - 达到上限时回调（可选）
 */
const AssetDrawer = ({
  isOpen,
  onClose,
  label,
  icon: Icon,
  options = [],
  selectedIds = [],
  onChange,
  variantIndexes = [],
  onVariantChange,
  maxSelection,
  onSelectionLimit
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [localVariantIndexes, setLocalVariantIndexes] = useState([]);
  const parsedMaxSelection = Number(maxSelection);
  const selectionLimit = Number.isFinite(parsedMaxSelection)
    ? Math.max(0, Math.floor(parsedMaxSelection))
    : null;
  const selectionLimitReached = selectionLimit !== null && localSelectedIds.length >= selectionLimit;

  // 初始化本地状态
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIds([...selectedIds]);
      setLocalVariantIndexes([...variantIndexes]);
      setSearchQuery("");
    }
  }, [isOpen, selectedIds, variantIndexes]);

  // 切换选中状态
  const toggleSelection = (id) => {
    const isSelected = localSelectedIds.includes(id);

    if (isSelected) {
      // 取消选中：移除ID和对应的变体索引
      const index = localSelectedIds.indexOf(id);
      const newSelectedIds = localSelectedIds.filter((_, i) => i !== index);
      const newVariantIndexes = localVariantIndexes.filter((_, i) => i !== index);

      setLocalSelectedIds(newSelectedIds);
      setLocalVariantIndexes(newVariantIndexes);
    } else {
      if (selectionLimitReached) {
        onSelectionLimit?.(selectionLimit);
        return;
      }
      // 选中：添加ID和初始化变体索引为-1
      setLocalSelectedIds([...localSelectedIds, id]);
      setLocalVariantIndexes([...localVariantIndexes, -1]);
    }
  };

  // 修改变体索引
  const handleVariantChange = (assetId, newVariantIndex) => {
    const index = localSelectedIds.indexOf(assetId);
    if (index === -1) return;

    const newVariantIndexes = [...localVariantIndexes];
    newVariantIndexes[index] = newVariantIndex;
    setLocalVariantIndexes(newVariantIndexes);
  };

  // 确认选择
  const handleConfirm = () => {
    onChange(localSelectedIds);
    if (onVariantChange) {
      onVariantChange(localVariantIndexes);
    }
    onClose();
  };

  // 取消
  const handleCancel = () => {
    onClose();
  };

  // 过滤资产
  const filteredOptions = options.filter(opt =>
    opt.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opt.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 已选中的资产
  const selectedAssets = localSelectedIds
    .map(id => options.find(opt => opt.id === id))
    .filter(Boolean);

  // 未选中的资产
  const unselectedAssets = filteredOptions.filter(opt => !localSelectedIds.includes(opt.id));

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      {/* 遮罩层 */}
      <div
        className={`fixed inset-0 bg-[var(--overlay-medium)] z-[9998] ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}
        onClick={handleCancel}
        style={{ backdropFilter: 'blur(2px)' }}
      />

      {/* 抽屉 - 从右侧滑入，宽度增加到 600px */}
      <div
        className={`fixed top-0 right-0 h-full w-[600px] z-[9999] flex flex-col shadow-2xl ${exiting ? "animate-out slide-out-to-right-4 duration-150" : "animate-in slide-in-from-right duration-300"}`}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderLeft: '1px solid var(--color-border-primary)'
        }}
      >
        {/* 头部 */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2">
            <Icon size={18} style={{ color: theme.colors.accentPrimary }} />
            <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              选择{label}
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-accent-light)', color: theme.colors.accentSecondary }}>
              {localSelectedIds.length}
            </span>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 搜索框 */}
        <div className="px-6 py-3 border-b shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`搜索${label}...`}
              className="w-full h-9 pl-10 pr-3 rounded-lg text-sm outline-none transition-all duration-200"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-primary)'
              }}
            />
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* 已选择区域 - 单列大图布局 */}
          {selectedAssets.length > 0 && (
            <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-muted)' }}>
                已选择 ({selectedAssets.length})
              </div>
              <div className="space-y-3">
                {selectedAssets.map((asset, index) => {
                  const variantIndex = localVariantIndexes[localSelectedIds.indexOf(asset.id)] ?? -1;

                  return (
                    <div
                      key={asset.id}
                      className="rounded-lg border transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: theme.colors.accentPrimary
                      }}
                    >
                      {/* 卡片头部 - 宽图按原始比例显示，根据选中的变体显示对应图片 */}
                      <div
                        className="relative cursor-pointer group overflow-hidden rounded-t-lg"
                        onClick={() => toggleSelection(asset.id)}
                        style={{ aspectRatio: '21 / 9' }}
                      >
                        {(() => {
                          // 根据变体索引获取对应的图片
                          let displayImage = asset.imageUrl;
                          if (variantIndex >= 0 && asset.variations?.[variantIndex]?.imageUrl) {
                            displayImage = asset.variations[variantIndex].imageUrl;
                          }

                          return displayImage ? (
                            <img
                              src={displayImage}
                              className="w-full h-full object-cover"
                              alt={asset.name}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                              <Icon size={48} style={{ opacity: 0.3, color: 'var(--color-text-muted)' }} />
                            </div>
                          );
                        })()}

                        {/* 选中标记 */}
                        <div className="absolute top-3 right-3">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: theme.colors.accentPrimary }}>
                            <CheckCircle2 size={18} style={{ color: 'white' }} />
                          </div>
                        </div>

                        {/* Hover 提示 */}
                        <div className="absolute inset-0 bg-[var(--overlay-medium)] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-sm font-medium text-white px-4 py-2 rounded-lg" style={{ backgroundColor: 'var(--overlay-medium)' }}>
                            点击取消选择
                          </span>
                        </div>
                      </div>

                      {/* 卡片内容 */}
                      <div className="p-4">
                        <div className="text-base font-bold mb-3 truncate" style={{ color: 'var(--color-text-primary)' }}>
                          {asset.name}
                        </div>

                        {/* 变体小图列表 */}
                        {asset.variations && asset.variations.length > 0 && onVariantChange && (
                          <div>
                            <label className="text-2xs mb-2 block font-medium" style={{ color: 'var(--color-text-muted)' }}>
                              选择变体：
                            </label>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {/* 主体图 */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVariantChange(asset.id, -1);
                                }}
                                className="shrink-0 flex flex-col items-center gap-1 rounded-lg p-1.5 transition-all"
                                style={{
                                  border: variantIndex === -1 ? `2px solid ${theme.colors.accentPrimary}` : '2px solid var(--color-border-primary)',
                                  backgroundColor: variantIndex === -1 ? `${theme.colors.accentPrimary}15` : 'var(--color-bg-overlay)'
                                }}
                              >
                                <div className="relative w-16 h-16 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                                  {asset.imageUrl ? (
                                    <img src={asset.imageUrl} className="w-full h-full object-cover" alt={asset.name} />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Icon size={20} style={{ opacity: 0.3, color: 'var(--color-text-muted)' }} />
                                    </div>
                                  )}
                                  {variantIndex === -1 && (
                                    <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-light)' }}>
                                      <CheckCircle2 size={16} style={{ color: theme.colors.accentPrimary }} />
                                    </div>
                                  )}
                                </div>
                                <span className="text-2xs max-w-[64px] truncate text-center" style={{ color: variantIndex === -1 ? theme.colors.accentPrimary : 'var(--color-text-secondary)' }}>
                                  主体图
                                </span>
                              </button>

                              {/* 变体列表 */}
                              {asset.variations.map((variant, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVariantChange(asset.id, idx);
                                  }}
                                  className="shrink-0 flex flex-col items-center gap-1 rounded-lg p-1.5 transition-all"
                                  style={{
                                    border: variantIndex === idx ? `2px solid ${theme.colors.accentPrimary}` : '2px solid var(--color-border-primary)',
                                    backgroundColor: variantIndex === idx ? `${theme.colors.accentPrimary}15` : 'var(--color-bg-overlay)'
                                  }}
                                >
                                  <div className="relative w-16 h-16 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                                    {variant.imageUrl ? (
                                      <img src={variant.imageUrl} className="w-full h-full object-cover" alt={variant.title} />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Icon size={20} style={{ opacity: 0.3, color: 'var(--color-text-muted)' }} />
                                      </div>
                                    )}
                                    {variantIndex === idx && (
                                      <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-light)' }}>
                                        <CheckCircle2 size={16} style={{ color: theme.colors.accentPrimary }} />
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-2xs max-w-[64px] truncate text-center" style={{ color: variantIndex === idx ? theme.colors.accentPrimary : 'var(--color-text-secondary)' }}>
                                    {variant.title}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 全部资产区域 - 单列大图布局 */}
          <div className="px-6 py-4">
            <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-muted)' }}>
              {searchQuery ? `搜索结果 (${unselectedAssets.length})` : `全部${label} (${unselectedAssets.length})`}
            </div>

            {unselectedAssets.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
                <Icon size={48} className="mx-auto mb-3 opacity-20" />
                <div className="text-sm">
                  {searchQuery ? '未找到匹配的资产' : `暂无${label}`}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {unselectedAssets.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => toggleSelection(asset.id)}
                    aria-disabled={selectionLimitReached}
                    className={`rounded-lg border transition-all ${selectionLimitReached ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:shadow-lg hover:scale-[1.01]"}`}
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border-primary)'
                    }}
                  >
                    {/* 卡片头部 - 宽图按原始比例显示 */}
                    <div className="relative group overflow-hidden rounded-t-lg" style={{ aspectRatio: '21 / 9' }}>
                      {asset.imageUrl ? (
                        <img
                          src={asset.imageUrl}
                          className="w-full h-full object-cover"
                          alt={asset.name}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                          <Icon size={48} style={{ opacity: 0.3, color: 'var(--color-text-muted)' }} />
                        </div>
                      )}

                      {/* 未选中标记 */}
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '2px solid white' }}>
                          <Circle size={18} style={{ color: 'white' }} />
                        </div>
                      </div>

                      {/* Hover 遮罩 */}
                      <div className="absolute inset-0 bg-[var(--overlay-light)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* 卡片内容 */}
                    <div className="p-4">
                      <div className="text-base font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>
                        {asset.name}
                      </div>
                      {asset.description && (
                        <div className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--color-text-muted)' }}>
                          {asset.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 底部按钮 */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t shrink-0"
          style={{
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)'
          }}
        >
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            已选择 <span className="font-bold" style={{ color: theme.colors.accentPrimary }}>{localSelectedIds.length}</span> 个{label}
            {selectionLimit !== null ? ` / 最多 ${selectionLimit} 个` : ""}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              style={{
                backgroundColor: 'var(--color-bg-overlay)',
                color: 'var(--color-text-secondary)'
              }}
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              className="px-6 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                backgroundColor: theme.colors.accentPrimary,
                color: 'white'
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AssetDrawer;
